import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n, intlTag } from "@/i18n";
import { Th, Td } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Download } from "lucide-react";

const ALL = "all";

const Flag = ({ ok, text, testId }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${ok ? "bg-primary/15 text-primary" : "bg-chart-3/20 text-chart-3"}`} data-testid={testId}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
    {text}
  </span>
);

/** Abschnitt D: Steuer-/Finanzexport (CSV je Bereich, XLSX je Bereich, XLSX "Alles in einer Datei"). */
export default function FinanceExport() {
  const { t, locale } = useI18n();
  // Jede Zeile wird in IHRER Währung formatiert — nie im Symbol der Basiswährung.
  const fmtMoney = (v, currency) => new Intl.NumberFormat(intlTag(locale), {
    style: "currency", currency: currency || "EUR",
  }).format(Number(v || 0));
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(String(nowY));
  const [month, setMonth] = useState("");
  const [dataset, setDataset] = useState(ALL);
  const [fmt, setFmt] = useState("xlsx");
  const [pre, setPre] = useState(null);
  const [denied, setDenied] = useState(false);
  const [periodError, setPeriodError] = useState("");
  const [busy, setBusy] = useState(false);

  const params = useCallback(() => {
    const p = { year };
    if (month) p.month = month;
    return p;
  }, [year, month]);

  // Ungültige Eingaben werden gar nicht erst angefragt — kein 400 in der Konsole.
  const localError = (() => {
    if (!/^\d{4}$/.test(String(year))) return t("fexp.invalidYear");
    if (month !== "" && !(Number(month) >= 1 && Number(month) <= 12)) return t("fexp.invalidMonth");
    return "";
  })();

  const load = useCallback(() => {
    if (localError) { setPre(null); setPeriodError(localError); return; }
    api.get("/finance/export/preview", { params: params() })
      .then((r) => { setPre(r.data); setDenied(false); setPeriodError(""); })
      .catch((e) => {
        setPre(null);
        setDenied(e?.response?.status === 403);
        setPeriodError(e?.response?.status === 403 ? "" : apiError(e));
      });
  }, [params, localError]);
  useEffect(() => { load(); }, [load]);

  const current = (pre?.datasets || []).find((d) => d.key === dataset);
  const csvBlocked = fmt === "csv" && (dataset === ALL || (current && !current.csv));

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/finance/export", {
        params: { ...params(), dataset, fmt }, responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `clubops_finance_${dataset}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("fexp.exported"));
    } catch (e) {
      toast.error(`${t("fexp.error")}: ${apiError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const pass = pre?.reconciliation_status === "PASS";

  return (
    <div className="space-y-6" data-testid="finance-export">
      <div>
        <h3 className="font-heading text-lg font-medium">{t("fexp.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t("fexp.subtitle")}</p>
      </div>

      {denied && <p className="text-sm text-muted-foreground" data-testid="fexp-denied">{t("fexp.noAccess")}</p>}
      {periodError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="fexp-period-error">{periodError}</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div><Label>{t("finc.year")}</Label>
          <Input className="mt-1.5 w-28" value={year} onChange={(e) => setYear(e.target.value)} data-testid="fexp-year" /></div>
        <div><Label>{t("finc.month")}</Label>
          <Input className="mt-1.5 w-24" placeholder="1-12" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="fexp-month" /></div>
        <div><Label>{t("fexp.scope")}</Label>
          <Select value={dataset} onValueChange={setDataset}>
            <SelectTrigger className="mt-1.5 w-60" data-testid="fexp-dataset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("fexp.allInOne")} (XLSX)</SelectItem>
              {(pre?.datasets || []).map((d) => (
                <SelectItem key={d.key} value={d.key}>{t(`fexp.ds.${d.key}`, d.sheet)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div><Label>{t("fexp.format")}</Label>
          <Select value={fmt} onValueChange={setFmt}>
            <SelectTrigger className="mt-1.5 w-28" data-testid="fexp-format"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="xlsx">XLSX</SelectItem><SelectItem value="csv">CSV</SelectItem></SelectContent>
          </Select>
        </div>
        <Button onClick={download} disabled={busy || csvBlocked || !pre} data-testid="fexp-download">
          <Download className="mr-2 h-4 w-4" />{busy ? t("fexp.exporting") : t("fexp.download")}
        </Button>
      </div>
      {csvBlocked && (
        <p className="text-xs text-chart-3" data-testid="fexp-csv-blocked">
          {dataset === ALL ? t("fexp.allXlsxOnly") : t("fexp.csvUnavailable")}
        </p>
      )}
      {current && <p className="text-xs text-muted-foreground" data-testid="fexp-rowcount">{t("fexp.rows", { count: current.rows })}</p>}

      {pre && (
        <div className="rounded-lg border border-border p-4 text-sm" data-testid="fexp-precheck">
          <strong>{t("fexp.beforeExport")}</strong>
          <div className="mt-3 flex flex-wrap gap-2">
            <Flag ok={!!pre.completeness?.shipping_cost?.is_shipping_cost_complete}
              text={pre.completeness?.shipping_cost?.is_shipping_cost_complete
                ? t("fexp.shipComplete")
                : t("fexp.shipIncomplete", {
                    final: pre.completeness?.shipping_cost?.shipping_cost_complete_count ?? 0,
                    total: pre.completeness?.shipping_cost?.fulfillment_count ?? 0,
                    missing: pre.completeness?.shipping_cost?.shipping_cost_missing_count ?? 0,
                    prov: pre.completeness?.shipping_cost?.shipping_cost_provisional_count ?? 0,
                  })}
              testId="fexp-shipping-flag" />
            <Flag ok={!!pre.completeness?.input_tax_complete}
              text={pre.completeness?.input_tax_complete
                ? t("fexp.inputComplete")
                : t("fexp.inputIncomplete", {
                    count: (pre.datasets || []).find((d) => d.key === "input_tax_incomplete")?.rows ?? 0,
                  })}
              testId="fexp-inputtax-flag" />
            <Flag ok={pass} text={pass ? t("fexp.statusPass") : t("fexp.statusFindings")}
              testId="fexp-reconciliation-flag" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("fexp.disclaimer")}</p>
          {dataset === "months" && pre.meta.months_note && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="fexp-months-note">{pre.meta.months_note}</p>
          )}
        </div>
      )}

      {pre && (
        <div className="overflow-x-auto rounded-lg border border-border" data-testid="fexp-reconciliation">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr>
              <Th>{t("fexp.metric")}</Th><Th right>{t("fexp.reportValue")}</Th>
              <Th right>{t("fexp.detailValue")}</Th><Th right>{t("fexp.delta")}</Th>
              <Th right>{t("fexp.count")}</Th><Th>{t("common.status")}</Th>
              <Th>{t("fexp.source")}</Th><Th>{t("fexp.notes")}</Th>
            </tr></thead>
            <tbody>
              {pre.reconciliation.map((r, i) => (
                <tr key={`${r.metric}-${r.currency}-${i}`} className="border-t border-border">
                  <Td>{r.metric} <span className="text-xs text-muted-foreground">{r.currency}</span></Td>
                  <Td right mono>{fmtMoney(r.report_value, r.currency)}</Td>
                  <Td right mono>{fmtMoney(r.detail_value, r.currency)}</Td>
                  <Td right mono className={r.delta === 0 ? "" : "font-semibold text-destructive"}>{fmtMoney(r.delta, r.currency)}</Td>
                  <Td right>{r.source_count}</Td>
                  <Td className={r.status === "PASS" ? "text-primary" : "font-medium text-destructive"}>{r.status}</Td>
                  <Td className="text-xs text-muted-foreground"><span className="font-mono">{r.source}</span></Td>
                  <Td className="max-w-[22rem] text-xs text-muted-foreground">{r.notes || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
